import React, { useState, useEffect } from 'react'
import logoE2IDefault from '../assets/Logo_lettre_droite.png'
import ProgressBar from './ProgressBar'
import StepConfigurator from './StepConfigurator'
import StepContact from './StepContact'
import StepEmailConfirmation from './StepEmailConfirmation'
import StepResults from './StepResults'
import Icon from './Icons'
import { calculateEstimate } from '../utils/pricingEngine'
import {
  sendConfirmationEmail,
  sendNotificationToE2I,
  generateToken,
  verifyToken,
} from '../services/emailService'

// ─── État initial ─────────────────────────────────────────────────────────────

const INITIAL_CONFIG = {
  prestation:  null,
  complexite:  null,
  volume:      null,
  urgence:     null,
  options:     [],
}

const INITIAL_CONTACT = {
  prenom:  '',
  nom:     '',
  societe: '',
  email:   '',
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function SimulateurDevis() {
  const [step,          setStep]          = useState(1)
  const [config,        setConfig]        = useState(INITIAL_CONFIG)
  const [contact,       setContact]       = useState(INITIAL_CONTACT)
  const [token,         setToken]         = useState(null)
  const [estimate,      setEstimate]      = useState(null)
  const [sendingEmail,  setSendingEmail]  = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendCount,   setResendCount]   = useState(0)

  // ── Vérifier si un token est présent dans l'URL (lien email) ────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlToken = params.get('e2i_token')
    if (urlToken) {
      handleUrlToken(urlToken)
    }
  }, [])

  async function handleUrlToken(urlToken) {
    const result = await verifyToken(urlToken)
    if (result.valid) {
      // Récupérer les données sauvegardées en localStorage (si disponibles)
      const saved = localStorage.getItem('e2i_devis_session')
      if (saved) {
        try {
          const session = JSON.parse(saved)
          const est = calculateEstimate(session.config)
          setConfig(session.config)
          setContact(session.contact)
          setEstimate(est)
          setStep(4)
          // Notifier E2I (la notification sera déclenchée ici car c'est le point de confirmation)
          sendNotificationToE2I({ contact: session.contact, config: session.config, estimate: est })
          // Nettoyer le token de l'URL sans recharger
          window.history.replaceState({}, '', window.location.pathname)
          return
        } catch (_) { /* ignore */ }
      }
      // Pas de session sauvegardée → aller à l'étape 1
    }
  }

  // ── Étape 1 → 2 ─────────────────────────────────────────────────────────────
  function handleConfigNext() {
    setStep(2)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ── Étape 2 → 3 (envoi email) ────────────────────────────────────────────────
  async function handleContactNext() {
    setSendingEmail(true)
    const newToken = generateToken()
    setToken(newToken)

    // Sauvegarder la session pour le retour depuis le lien email
    localStorage.setItem(
      'e2i_devis_session',
      JSON.stringify({ config, contact }),
    )

    await sendConfirmationEmail({
      email:  contact.email,
      prenom: contact.prenom,
      nom:    contact.nom,
      token:  newToken,
    })

    // Notification immédiate à E2I dès la demande du code
    sendNotificationToE2I({ contact, config, estimate: calculateEstimate(config) })

    setSendingEmail(false)
    setStep(3)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ── Renvoi email ──────────────────────────────────────────────────────────────
  async function handleResend() {
    if (resendCount >= 3) return
    setResendLoading(true)
    await sendConfirmationEmail({
      email:  contact.email,
      prenom: contact.prenom,
      nom:    contact.nom,
      token,
    })
    setResendCount((c) => c + 1)
    setResendLoading(false)
  }

  // ── Étape 3 → 4 (confirmation) ───────────────────────────────────────────────
  async function handleEmailConfirmed() {
    const est = calculateEstimate(config)
    setEstimate(est)
    await sendNotificationToE2I({ contact, config, estimate: est })
    localStorage.removeItem('e2i_devis_session')
    setStep(4)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ── Recommencer ───────────────────────────────────────────────────────────────
  function handleReset() {
    setStep(1)
    setConfig(INITIAL_CONFIG)
    setContact(INITIAL_CONTACT)
    setToken(null)
    setEstimate(null)
    setResendCount(0)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ── Rendu ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen py-8 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto">

        {/* En-tête E2I Solutions */}
        <header className="text-center mb-8">
          <div className="mb-4">
            <img
              src={window.E2ISimuConfig?.assetsUrl ? window.E2ISimuConfig.assetsUrl + 'Logo_lettre_droite.png' : logoE2IDefault}
              alt="E2I Solutions"
              className="h-16 w-auto mx-auto"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
            Simulateur de devis
          </h1>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Obtenez une estimation de votre projet électronique en quelques minutes.
          </p>
        </header>

        {/* Carte principale */}
        <div className="bg-white rounded-2xl shadow-[0_2px_24px_0_rgb(0_0_0_/_0.08)] border border-slate-100 overflow-hidden">

          {/* Barre de progression */}
          <div className="px-6 sm:px-8 pt-7 pb-6 border-b border-slate-100 bg-slate-50/60">
            <ProgressBar currentStep={step} />
          </div>

          {/* Contenu de l'étape */}
          <div className="px-6 sm:px-8 py-8">

            {/* Titre dynamique de l'étape */}
            {step !== 4 && (
              <div className="mb-7">
                <StepTitle step={step} />
              </div>
            )}

            {step === 1 && (
              <StepConfigurator
                config={config}
                onChange={setConfig}
                onNext={handleConfigNext}
              />
            )}

            {step === 2 && (
              <StepContact
                contact={contact}
                onChange={setContact}
                onNext={handleContactNext}
                onBack={() => setStep(1)}
                loading={sendingEmail}
              />
            )}

            {step === 3 && (
              <StepEmailConfirmation
                email={contact.email}
                onResend={handleResend}
                onConfirm={handleEmailConfirmed}
                resendLoading={resendLoading}
                resendCount={resendCount}
              />
            )}

            {step === 4 && (
              <StepResults
                estimate={estimate}
                config={config}
                contact={contact}
                onReset={handleReset}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center mt-8 space-y-1">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} E2I Solutions — Bureau d'études électronique
          </p>
          <p className="text-xs text-slate-400">
            <a
              href="mailto:contact@e2i-solutions.com"
              className="hover:text-blue-500 transition-colors"
            >
              contact@e2i-solutions.com
            </a>
          </p>
        </footer>
      </div>
    </div>
  )
}

// ─── Titres d'étape ───────────────────────────────────────────────────────────

function StepTitle({ step }) {
  const titles = {
    1: {
      title:    'Configurez votre projet',
      subtitle: 'Sélectionnez les caractéristiques de votre besoin pour obtenir une estimation adaptée.',
    },
    2: {
      title:    'Vos coordonnées',
      subtitle: "Renseignez vos informations pour recevoir l'estimation par email.",
    },
    3: {
      title:    'Confirmation email',
      subtitle: "Vérifiez votre messagerie et confirmez votre adresse pour afficher l'estimation.",
    },
  }
  const t = titles[step]
  if (!t) return null

  return (
    <div>
      <h2 className="text-lg font-bold text-slate-800">{t.title}</h2>
      <p className="text-sm text-slate-500 mt-1">{t.subtitle}</p>
    </div>
  )
}
